import FormStore from '~/lib/store/FormStore';
import {
    ContactDetails,
    ContactDetailsRequest,
    PhoneVerificationRequest,
    SetupPhoneVerificationRequest,
} from '~/lib/ApiModel';
import { field, validate } from '~/lib/helper/ValidationHelper';
import { is } from '~/lib/helper/ValidationHelper';
import { ValidationErrors } from '~/lib/helper/ErrorHelper';

interface ContactInformationFields extends ContactDetailsRequest {
    hash: string;
    code: string;
}

function validateContactDetails(
    values: ContactInformationFields,
    onlyFields?: (keyof ContactInformationFields)[]
): ValidationErrors | undefined {
    const result = validate(
        values,
        field('name', is.requiredNotEmpty('Name is required')),
        field('phone_number', is.requiredNotEmpty('Phone number is required')),
        field('address1', is.requiredNotEmpty('Address is required')),
        field('city', is.requiredNotEmpty('City is required')),
        field('state', is.requiredNotEmpty('State is required')),
        field('postal_code', is.requiredNotEmpty('Postal code is required')),
        field('country', is.requiredNotEmpty('Country is required')),
        field('email', is.requiredNotEmpty('Email is required'))
    );
    if (!result) return result;
    if (!onlyFields) return result;

    for (const field of Object.keys(result) as (keyof ContactInformationFields)[]) {
        if (!onlyFields.includes(field)) {
            delete result[field];
        }
    }

    return result;
}

export default class ContactInformationStore extends FormStore<ContactInformationFields> {
    static defaultValues(): ContactInformationFields {
        return {
            name: '',
            organization: '',
            phone_number: '',
            address1: '',
            address2: '',
            city: '',
            state: '',
            postal_code: '',
            country: '',
            email: '',
            invoice_email: '',
            hash: '',
            code: '',
        };
    }

    constructor() {
        super(ContactInformationStore.defaultValues());
    }

    initFromContactDetails(contactDetails: ContactDetails): void {
        this.init({
            name: contactDetails.name,
            organization: contactDetails.organization || '',
            phone_number: contactDetails.phone_number,
            address1: contactDetails.address1,
            address2: contactDetails.address2 || '',
            city: contactDetails.city,
            state: contactDetails.state,
            postal_code: contactDetails.postal_code,
            country: contactDetails.country,
            email: contactDetails.email,
            invoice_email: contactDetails.invoice_email || '',
            hash: '',
            code: '',
        });
    }

    validate() {
        return super.validate((values) => validateContactDetails(values));
    }

    validateFields(fields: (keyof ContactInformationFields)[]) {
        return super.validate((values) => validateContactDetails(values, fields));
    }

    asContactDetailsRequest(): ContactDetailsRequest {
        const { hash, code, ...contactDetails } = this.values;
        if (contactDetails.invoice_email === '') contactDetails.invoice_email = null;
        return contactDetails;
    }

    asPhoneVerificationRequest(): PhoneVerificationRequest {
        return {
            code: this.values.code,
            hash: this.values.hash,
        };
    }

    asSetupPhoneVerificationRequest(): SetupPhoneVerificationRequest {
        return {
            phone_number: this.values.phone_number,
            country: this.values.country,
        };
    }
}
